// Currency-aware number-in-words helper for CTC inputs. Display-only — it reflects the exact
// digits typed, so a candidate who enters "12" sees "twelve" (obviously not a salary) and
// self-corrects to the absolute figure. INR uses Indian grouping (lakh/crore); every other
// currency uses the Western short scale (thousand/million/billion). Shown under the candidate
// CTC field and the recruiter's qualifying-value field so both sides read the same units.

const ONES = [
    "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
    "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen",
];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

// 0..999 → words (no scale word)
const threeDigits = (n) => {
    const out = [];
    if (n >= 100) { out.push(ONES[Math.floor(n / 100)], "hundred"); n %= 100; }
    if (n >= 20) { out.push(TENS[Math.floor(n / 10)]); if (n % 10) out.push(ONES[n % 10]); }
    else if (n > 0) out.push(ONES[n]);
    return out.join(" ");
};

// 0..99 → words (used for the two-digit lakh/crore groups in Indian numbering)
const twoDigits = (n) => {
    if (n >= 20) { const t = TENS[Math.floor(n / 10)]; return n % 10 ? `${t} ${ONES[n % 10]}` : t; }
    return ONES[n];
};

const westernWords = (n) => {
    if (n === 0) return "zero";
    const scales = ["", "thousand", "million", "billion", "trillion"];
    const parts = [];
    let i = 0;
    while (n > 0 && i < scales.length) {
        const chunk = n % 1000;
        if (chunk) parts.unshift(`${threeDigits(chunk)}${scales[i] ? ` ${scales[i]}` : ""}`);
        n = Math.floor(n / 1000);
        i += 1;
    }
    return parts.join(" ");
};

const indianWords = (n) => {
    if (n === 0) return "zero";
    const groups = ["thousand", "lakh", "crore", "arab"]; // two-digit groups above the last three
    const parts = [];
    const last3 = n % 1000;
    let rest = Math.floor(n / 1000);
    let i = 0;
    while (rest > 0 && i < groups.length) {
        const chunk = rest % 100;
        if (chunk) parts.unshift(`${twoDigits(chunk)} ${groups[i]}`);
        rest = Math.floor(rest / 100);
        i += 1;
    }
    if (last3) parts.push(threeDigits(last3));
    return parts.join(" ");
};

const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

// Returns e.g. "Twelve Lakh INR" / "One Hundred Twenty Thousand USD"; "" for blank/zero/invalid.
export const numberToWords = (value, currency) => {
    if (value === null || value === undefined || value === "") return "";
    const whole = Math.floor(Number(String(value).replace(/[^\d.]/g, "")));
    if (!Number.isFinite(whole) || whole <= 0) return "";
    const words = titleCase(currency === "INR" ? indianWords(whole) : westernWords(whole));
    return currency ? `${words} ${currency}` : words;
};
